import { REVIEW_SUMMARY_PUBLIC_ENDPOINT } from '@app/pages/api/constants';
import { ReviewSummaryAPIResponse } from '@app/pages/api/review-summary/types';
import { ReviewSummary } from '@app/types/shop-bff/types';
import { getSafeUrl } from '@redbubble/utils/getSafeUrl';
import { useCallback, useState } from 'react';

export interface GetReviewSummaryVariables {
  inventoryItemId: string;
  locale: string;
}

type ReviewSummaryState = {
  reviewSummary?: ReviewSummary | null;
  loading: boolean;
};
// cache to not call API after repeated co-listing switches
const cache = new Map<string, ReviewSummaryState>();

const fetchReviewSummary = async (
  variables: GetReviewSummaryVariables
): Promise<ReviewSummary | null> => {
  const url = getSafeUrl(REVIEW_SUMMARY_PUBLIC_ENDPOINT, window.location.href);

  if (!url) {
    throw new Error('Invalid URL for review summary endpoint');
  }

  url.searchParams.set('inventoryItemId', variables.inventoryItemId);
  url.searchParams.set('locale', variables.locale);

  const response = await fetch(url, { method: 'GET' });
  const jsonData: ReviewSummaryAPIResponse = await response.json();

  if (!response.ok) {
    const errorMessage =
      jsonData && typeof jsonData === 'object' && 'error' in jsonData
        ? jsonData.error
        : 'Failed to fetch review summary';
    throw new Error(errorMessage);
  }

  if (jsonData && typeof jsonData === 'object' && 'error' in jsonData) {
    throw new Error(jsonData.error);
  }

  return jsonData;
};

export const useReviewSummary = (
  initialProductTypeId: string,
  initialReviewSummary?: ReviewSummary | null
) => {
  const [{ reviewSummary, loading }, setReviewSummary] = useState<{
    reviewSummary?: ReviewSummary | null;
    loading: boolean;
  }>({
    reviewSummary: initialReviewSummary,
    loading: !initialReviewSummary,
  });
  cache.set(initialProductTypeId, {
    reviewSummary: initialReviewSummary,
    loading: !initialReviewSummary,
  });

  const refresh = useCallback(
    (variables: GetReviewSummaryVariables, productTypeId: string) => {
      const cachedReviewSummary = cache.get(productTypeId);
      if (cachedReviewSummary) {
        setReviewSummary(cachedReviewSummary);
      } else {
        setReviewSummary({ reviewSummary: null, loading: true });
        fetchReviewSummary(variables)
          .then((res) => {
            cache.set(productTypeId, {
              reviewSummary: res,
              loading: false,
            });
            setReviewSummary({
              reviewSummary: res,
              loading: false,
            });
          })
          .catch(() => {
            // TODO: Handle errors
            setReviewSummary({
              reviewSummary: null,
              loading: false,
            });
          });
      }
    },
    []
  );

  return {
    reviewSummary,
    loading,
    refresh,
  };
};
